import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _callSuper from "@babel/runtime/helpers/esm/callSuper";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import React from 'react';
import PropTypes from 'prop-types';
import { createRoot } from 'react-dom/client';
import t from 'format-message';
import { Alert } from '@instructure/ui-alerts';
import { Link } from '@instructure/ui-link';
import { Text } from '@instructure/ui-text';
import { PresentationContent, ScreenReaderContent } from '@instructure/ui-a11y-content';
import { Transition } from '@instructure/ui-motion';
import { findDetailMessage, isLoadingChunkError } from "../../util/flashAlertUtils.js";
/*
 * Note: This file is copied over from canvas-lms/ui/shared/alerts/react/FlashAlert.js
 * It has a few minor changes so that it works with outcomes-ui, but is overall
 * pretty much the same and it works the same. Some functions were moved into a utils
 * file for testing purposes.
 */
var messageHolderId = 'flashalert_message_holder'; // specs fail if I reuse jquery's elements
var screenreaderMessageHolderId = 'flash_screenreader_holder';
export var defaultTimeout = 10000;
export function showFlashAlert(_ref) {
  var message = _ref.message,
    err = _ref.err,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? err ? 'error' : 'info' : _ref$type,
    _ref$timeout = _ref.timeout,
    timeout = _ref$timeout === void 0 ? defaultTimeout : _ref$timeout,
    _ref$srOnly = _ref.srOnly,
    srOnly = _ref$srOnly === void 0 ? false : _ref$srOnly;
  function closeAlert(atNode, root) {
    if (root) {
      root.unmount();
    }
    atNode.remove();
  }
  function getAlertContainer() {
    var alertContainer = document.getElementById(messageHolderId);
    if (!alertContainer) {
      alertContainer = document.createElement('div');
      alertContainer.classList.add('clickthrough-container');
      alertContainer.id = messageHolderId;
      alertContainer.setAttribute('style', 'position: fixed; top: 0; left: 0; width: 100%; z-index: 100000;');
      document.body.appendChild(alertContainer);
    }
    return alertContainer;
  }
  function renderAlert(parent) {
    var root = createRoot(parent);
    root.render(/*#__PURE__*/React.createElement(FlashAlert, {
      message: message,
      timeout: timeout,
      error: err,
      variant: type,
      onClose: closeAlert.bind(null, parent, root),
      screenReaderOnly: srOnly
    }));
  }
  var div = document.createElement('div');
  // div.setAttribute('class', styles.flashMessage)
  div.setAttribute('style', 'max-width:50em;margin:1rem auto;');
  div.setAttribute('class', 'flashalert-message');
  getAlertContainer().appendChild(div);
  renderAlert(div);
}
export function destroyContainer() {
  var container = document.getElementById(messageHolderId);
  var liveRegion = document.getElementById(screenreaderMessageHolderId);
  if (container) container.remove();
  if (liveRegion) liveRegion.remove();
}
export function showFlashError() {
  var message = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : t('An error occurred making a network request');
  return function (err) {
    return showFlashAlert({
      message: message,
      err: err,
      type: 'error'
    });
  };
}
export function showFlashSuccess(message) {
  return function () {
    return showFlashAlert({
      message: message,
      type: 'success'
    });
  };
}

// An Alert with a message and "Details" button which surfaces
// more info about the error when pressed.
// Is displayed at the top of the document, and will close itself after a while
var FlashAlert = /*#__PURE__*/function (_React$Component) {
  function FlashAlert(props) {
    var _this;
    _classCallCheck(this, FlashAlert);
    _this = _callSuper(this, FlashAlert, [props]);
    _this.showDetails = function () {
      _this.setState({
        showDetails: true
      });
      clearTimeout(_this.timerId);
      _this.timerId = setTimeout(function () {
        return _this.closeAlert();
      }, _this.props.timeout);
    };
    _this.closeAlert = function () {
      _this.setState({
        isOpen: false
      }, function () {
        setTimeout(function () {
          clearTimeout(_this.timerId);
          _this.props.onClose();
        }, 500);
      });
    };
    _this.state = {
      showDetails: false,
      isOpen: true
    };
    _this.timerId = 0;
    return _this;
  }
  _inherits(FlashAlert, _React$Component);
  return _createClass(FlashAlert, [{
    key: "getLiveRegion",
    value: function getLiveRegion() {
      // return element where flash screenreader messages go.
      // create if necessary
      var liveRegion = document.getElementById(screenreaderMessageHolderId);
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = screenreaderMessageHolderId;
        liveRegion.setAttribute('role', 'alert');
        document.body.appendChild(liveRegion);
      }
      return liveRegion;
    }
  }, {
    key: "renderDetailMessage",
    value: function renderDetailMessage(a, b) {
      var showPrimaryDetails = a && !isLoadingChunkError(a);
      var showSecondaryDetails = b && !isLoadingChunkError(b);
      return /*#__PURE__*/React.createElement(Text, {
        as: "p",
        fontStyle: "italic"
      }, showPrimaryDetails && /*#__PURE__*/React.createElement(Text, null, a), showSecondaryDetails && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Text, null, b)));
    }
  }, {
    key: "render",
    value: function render() {
      var details = null;
      if (this.props.error) {
        var _findDetailMessage = findDetailMessage(this.props.error),
          a = _findDetailMessage.a,
          b = _findDetailMessage.b;
        var showPrimaryDetails = a && !isLoadingChunkError(a);
        var showSecondaryDetails = b && !isLoadingChunkError(b);
        if (showPrimaryDetails || showSecondaryDetails) {
          if (this.state.showDetails) {
            details = this.renderDetailMessage(a, b);
          } else {
            details = /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(PresentationContent, null, /*#__PURE__*/React.createElement(Link, {
              isWithinText: false,
              as: "button",
              onClick: this.showDetails
            }, t('Details'))), /*#__PURE__*/React.createElement(ScreenReaderContent, null, this.renderDetailMessage(a, b)));
          }
        }
      }
      return /*#__PURE__*/React.createElement(Transition, {
        transitionOnMount: true,
        in: this.state.isOpen,
        type: "fade"
      }, /*#__PURE__*/React.createElement(Alert, {
        variant: this.props.variant,
        renderCloseButtonLabel: t('Close'),
        onDismiss: this.closeAlert,
        margin: "small auto",
        timeout: this.props.timeout,
        liveRegion: this.getLiveRegion,
        transition: "fade",
        screenReaderOnly: this.props.screenReaderOnly
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
        style: {
          margin: '0 -5px'
        }
      }, this.props.message), details)));
    }
  }]);
  FlashAlert.displayName = "FlashAlert";
}(React.Component);
FlashAlert.propTypes = {
  onClose: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
  error: PropTypes.instanceOf(Error),
  variant: PropTypes.oneOf(['info', 'success', 'warning', 'error']),
  timeout: PropTypes.number,
  screenReaderOnly: PropTypes.bool
};
FlashAlert.defaultProps = {
  error: null,
  variant: 'info',
  timeout: defaultTimeout,
  screenReaderOnly: false
};
export default FlashAlert;